import { useEffect, useState } from "react";
import { clientsApi, adminApi, auth } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, Radio, LogOut, Clock, Copy } from "lucide-react";

const DEFAULT_BG_IMAGE = "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=2200&q=80&auto=format&fit=crop";
const BRAND_DEFAULTS = {
  primary_color: "#102713",
  secondary_color: "#F4EDE0",
  accent_color: "#C8A64B",
  background_image_url: DEFAULT_BG_IMAGE,
  font_family: "serif",
};
const EMPTY = { name: "", slug: "", logoUrl: "", displayTitle: "", embedCode: "", starts_at: "", status_note: "", title_logo_url: "", is_active: true, ...BRAND_DEFAULTS };

const pickBrand = (c) => ({
  primary_color: c.primary_color || BRAND_DEFAULTS.primary_color,
  secondary_color: c.secondary_color || BRAND_DEFAULTS.secondary_color,
  accent_color: c.accent_color || BRAND_DEFAULTS.accent_color,
  background_image_url: c.background_image_url || BRAND_DEFAULTS.background_image_url,
  font_family: c.font_family || BRAND_DEFAULTS.font_family,
});

const ColorField = ({ label, value, onChange, testid }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={onChange}
        data-testid={`field-color-${testid}`}
        className="h-9 w-10 flex-none rounded border border-border/60 bg-transparent cursor-pointer p-0"
        aria-label={`${label} color picker`}
      />
      <Input value={value} onChange={onChange} className="font-mono text-xs uppercase" data-testid={`field-hex-${testid}`} />
    </div>
  </div>
);

// ISO (UTC) <-> datetime-local string helpers
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (val) => (val ? new Date(val).toISOString() : null);

const LoginGate = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { token } = await adminApi.login(password);
      auth.set(token);
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="wl-admin min-h-screen flex items-start justify-center pt-16 md:pt-24 bg-background text-foreground px-6" data-testid="admin-login">
      <Toaster position="top-center" richColors />
      <form onSubmit={submit} className="w-full max-w-sm rounded-xl border border-border/60 bg-card p-8 space-y-6">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-11 w-11 rounded-full bg-primary text-primary-foreground">
            <Radio size={22} />
          </span>
          <div>
            <h1 className="font-[Oswald] uppercase tracking-widest text-xl leading-none text-foreground">The Stream Stop</h1>
            <p className="text-xs text-muted-foreground mt-1 font-[Poppins]">Admin Access</p>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="pw">Password</Label>
          <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter admin password" data-testid="login-password" autoFocus />
        </div>
        <Button type="submit" disabled={busy} data-testid="login-btn" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-[Oswald] uppercase tracking-wider">
          {busy ? "Signing in…" : "Sign In"}
        </Button>
      </form>
    </div>
  );
};

const Dashboard = ({ onLogout }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => {
    setLoading(true);
    clientsApi.list().then(setClients).catch(() => toast.error("Failed to load clients")).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, slug: c.slug, logoUrl: c.logoUrl,
      displayTitle: c.displayTitle, embedCode: c.embedCode,
      starts_at: toLocalInput(c.starts_at), status_note: c.status_note || "",
      title_logo_url: c.title_logo_url || "",
      is_active: c.is_active !== false,
      ...pickBrand(c),
    });
    setDialogOpen(true);
  };
  const openDuplicate = (c) => {
    setEditing(null);
    setForm({
      name: `${c.name} (Copy)`, slug: "", logoUrl: c.logoUrl,
      displayTitle: c.displayTitle, embedCode: c.embedCode,
      starts_at: toLocalInput(c.starts_at), status_note: c.status_note || "",
      title_logo_url: c.title_logo_url || "",
      is_active: c.is_active !== false,
      ...pickBrand(c),
    });
    setDialogOpen(true);
  };

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleAuthError = (err) => {
    if (err?.response?.status === 401) {
      toast.error("Session expired. Please sign in again.");
      auth.clear();
      onLogout();
      return true;
    }
    return false;
  };

  const save = async () => {
    if (!form.name.trim() || !(form.slug.trim() || form.name.trim())) {
      toast.error("Name and slug are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, starts_at: fromLocalInput(form.starts_at) };
      if (editing) {
        await clientsApi.update(editing.id, payload);
        toast.success("Client updated");
      } else {
        await clientsApi.create(payload);
        toast.success("Client created");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      if (!handleAuthError(err)) toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const t = deleteTarget;
    setDeleteTarget(null);
    try {
      await clientsApi.remove(t.id);
      toast.success("Client deleted");
      load();
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Delete failed");
    }
  };

  const toggleActive = async (c) => {
    const next = !(c.is_active !== false);
    setClients((prev) => prev.map((x) => (x.id === c.id ? { ...x, is_active: next } : x)));
    try {
      await clientsApi.update(c.id, { is_active: next });
      toast.success(next ? "Site activated" : "Site deactivated — visitors will be redirected");
    } catch (err) {
      setClients((prev) => prev.map((x) => (x.id === c.id ? { ...x, is_active: !next } : x)));
      if (!handleAuthError(err)) toast.error("Could not update status");
    }
  };

  const fmtSchedule = (iso) => {
    if (!iso) return <span className="text-muted-foreground">Live now</span>;
    const d = new Date(iso);
    const upcoming = d.getTime() > Date.now();
    return (
      <span className={`inline-flex items-center gap-1 ${upcoming ? "text-primary" : "text-muted-foreground"}`}>
        <Clock size={13} /> {d.toLocaleString()}
      </span>
    );
  };

  return (
    <div className="wl-admin min-h-screen bg-background text-foreground" data-testid="admin-dashboard">
      <Toaster position="top-center" richColors />
      <header className="border-b border-border/60 bg-card">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-10 w-10 rounded-full bg-primary text-primary-foreground">
              <Radio size={20} />
            </span>
            <div>
              <h1 className="font-[Oswald] uppercase tracking-widest text-xl leading-none text-foreground">The Stream Stop</h1>
              <p className="text-xs text-muted-foreground mt-1 font-[Poppins]">Client Livestream Manager</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={openCreate} data-testid="add-client-btn" className="bg-primary text-primary-foreground hover:bg-primary/90 font-[Oswald] uppercase tracking-wider">
              <Plus size={18} className="mr-1" /> Add Client
            </Button>
            <Button variant="outline" size="icon" onClick={onLogout} data-testid="logout-btn" title="Log out">
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="rounded-xl border border-border/60 bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border/60">
                <TableHead className="text-foreground font-[Oswald] uppercase tracking-wider">Name</TableHead>
                <TableHead className="text-foreground font-[Oswald] uppercase tracking-wider">Slug</TableHead>
                <TableHead className="text-foreground font-[Oswald] uppercase tracking-wider">Display Title</TableHead>
                <TableHead className="text-foreground font-[Oswald] uppercase tracking-wider">Goes Live</TableHead>
                <TableHead className="text-foreground font-[Oswald] uppercase tracking-wider">Active</TableHead>
                <TableHead className="text-right text-foreground font-[Oswald] uppercase tracking-wider">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : clients.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No clients yet. Add your first one.</TableCell></TableRow>
              ) : clients.map((c) => {
                const active = c.is_active !== false;
                return (
                <TableRow key={c.id} className={`border-border/60 ${active ? "" : "opacity-60"}`} data-testid={`client-row-${c.slug}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell>
                    <a href={`/${c.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`view-${c.slug}`}>
                      /{c.slug} <ExternalLink size={13} />
                    </a>
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-xs truncate">{c.displayTitle}</TableCell>
                  <TableCell className="text-sm">{fmtSchedule(c.starts_at)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={active} onCheckedChange={() => toggleActive(c)} data-testid={`active-toggle-${c.slug}`} aria-label="Toggle site active" />
                      <span className={`text-xs font-[Poppins] ${active ? "text-primary" : "text-muted-foreground"}`} data-testid={`active-label-${c.slug}`}>
                        {active ? "Active" : "Redirecting"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="icon" onClick={() => openDuplicate(c)} data-testid={`duplicate-${c.slug}`} title="Duplicate">
                      <Copy size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-${c.slug}`} title="Edit">
                      <Pencil size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(c)} data-testid={`delete-${c.slug}`} title="Delete" className="text-destructive hover:text-destructive">
                      <Trash2 size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </div>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="wl-admin bg-card border-border text-foreground max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Oswald] uppercase tracking-wider">{editing ? "Edit Client" : "Add Client"}</DialogTitle>
            <DialogDescription>Manage the client's page content, schedule and embed code.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={form.name} onChange={setField("name")} placeholder="Bass Pro Shops" data-testid="field-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">URL Slug</Label>
              <Input id="slug" value={form.slug} onChange={setField("slug")} placeholder="bassproclub" data-testid="field-slug" />
              <p className="text-xs text-muted-foreground">Page will be at /{form.slug || "your-slug"}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="logoUrl">Logo URL</Label>
              <Input id="logoUrl" value={form.logoUrl} onChange={setField("logoUrl")} placeholder="https://…/logo.png" data-testid="field-logo" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="titleLogoUrl">Title Logo URL (optional)</Label>
              <Input id="titleLogoUrl" value={form.title_logo_url} onChange={setField("title_logo_url")} placeholder="https://…/secondary-logo.png" data-testid="field-titlelogo" />
              <p className="text-xs text-muted-foreground">Small logo shown to the left of the display title.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="displayTitle">Display Title</Label>
              <Input id="displayTitle" value={form.displayTitle} onChange={setField("displayTitle")} placeholder="2026 Leadership Journey Live Q&A" data-testid="field-title" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="startsAt">Goes Live At (optional)</Label>
              <Input id="startsAt" type="datetime-local" value={form.starts_at} onChange={setField("starts_at")} data-testid="field-startsat" />
              <p className="text-xs text-muted-foreground">Before this time the page shows a countdown. Leave blank to go live immediately.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="statusNote">Status Note (optional)</Label>
              <Input id="statusNote" value={form.status_note} onChange={setField("status_note")} placeholder="e.g. Doors open 15 minutes early" data-testid="field-statusnote" />
              <p className="text-xs text-muted-foreground">Shown under the countdown timer on the public page.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="embedCode">Embed Code</Label>
              <Textarea id="embedCode" value={form.embedCode} onChange={setField("embedCode")} rows={7} placeholder="<div>…<iframe …></iframe></div>" className="font-mono text-xs" data-testid="field-embed" />
            </div>

            <div className="pt-2 border-t border-border/60">
              <h3 className="font-[Oswald] uppercase tracking-wider text-sm text-primary mt-3 mb-1">Branding</h3>
              <p className="text-xs text-muted-foreground mb-3">Colors, background and font for this client's public page. Defaults match the Bass Pro forest look.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <ColorField label="Primary" testid="primary" value={form.primary_color} onChange={setField("primary_color")} />
                <ColorField label="Secondary (text)" testid="secondary" value={form.secondary_color} onChange={setField("secondary_color")} />
                <ColorField label="Accent (highlights)" testid="accent" value={form.accent_color} onChange={setField("accent_color")} />
              </div>
              <div className="space-y-2 mt-3">
                <Label htmlFor="bgUrl">Background Image URL</Label>
                <div className="flex items-center gap-3">
                  <Input id="bgUrl" value={form.background_image_url} onChange={setField("background_image_url")} placeholder="https://…/background.jpg" data-testid="field-bgimage" />
                  {form.background_image_url && (
                    <img src={form.background_image_url} alt="" className="h-9 w-14 rounded object-cover border border-border/60 flex-none" />
                  )}
                </div>
              </div>
              <div className="space-y-2 mt-3">
                <Label>Font Family</Label>
                <div className="flex gap-2" data-testid="field-font">
                  {["serif", "sans"].map((f) => (
                    <Button key={f} type="button" variant={form.font_family === f ? "default" : "outline"}
                      onClick={() => setForm((prev) => ({ ...prev, font_family: f }))}
                      data-testid={`font-${f}`}
                      className={form.font_family === f ? "bg-primary text-primary-foreground hover:bg-primary/90 capitalize" : "capitalize"}>
                      {f}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-client-btn" className="bg-primary text-primary-foreground hover:bg-primary/90">
              {saving ? "Saving…" : editing ? "Save Changes" : "Create Client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="wl-admin bg-card border-border text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the client and its page at /{deleteTarget?.slug}.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const AdminDashboard = () => {
  const [authed, setAuthed] = useState(!!auth.get());
  const [checking, setChecking] = useState(!!auth.get());

  useEffect(() => {
    if (!auth.get()) { setChecking(false); return; }
    adminApi.verify().then(() => setAuthed(true)).catch(() => { auth.clear(); setAuthed(false); }).finally(() => setChecking(false));
  }, []);

  const logout = () => { auth.clear(); setAuthed(false); };

  if (checking) {
    return <div className="wl-admin min-h-screen grid place-items-center bg-background text-muted-foreground">Loading…</div>;
  }
  if (!authed) return <LoginGate onSuccess={() => setAuthed(true)} />;
  return <Dashboard onLogout={logout} />;
};

export default AdminDashboard;
